import {
  Heading,
  HStack,
  Tab,
  TabList,
  Tabs,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ValleyBankCustomerWithRelationships } from '@finley-technologies/valley-bank-utils';
import { parse } from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankCustomerQuery } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { colors } from '~/packages/common-frontend/colors';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { ArrangementGroupSummaryLink } from '~/valleyBank/domains/ArrangementGroups/hooks/ArrangementGroupsContext';
import { getArrangementGroupNumbers } from '~/valleyBank/domains/Collateral/ui/CollateralAccountArrangementGroupNumbers';
import {
  CustomerSummaryLink,
  useCustomerUrlParam,
} from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

const TABS = [
  {
    key: 'summary' as const,
    label: 'Summary',
    route: '/valley-bank/customers/summary',
  },
  {
    key: 'holdings' as const,
    label: 'Holdings',
    route: '/valley-bank/customers/holdings',
  },
  {
    key: 'info' as const,
    label: 'Customer Info',
    route: '/valley-bank/customers/info',
  },
];

export type CustomerDetailsTab = typeof TABS[number]['key'];

const findArrangementGroupNumbers = (
  customer: ValleyBankCustomerWithRelationships,
): string[] => {
  const arrangementGroupNumbers = new Set<string>();

  const receivableArrangementGroups: string[] = getArrangementGroupNumbers(
    customer.collateralAccounts.flatMap(({ receivables }) => receivables),
  );

  receivableArrangementGroups.forEach((arrangementGroup) =>
    arrangementGroupNumbers.add(arrangementGroup),
  );

  const loanAccountsWithArrangementGroups: string[] = _.compact(
    customer.loanAccounts.map(({ arrangementGroupId }) => arrangementGroupId),
  );

  loanAccountsWithArrangementGroups.forEach((arrangementGroup) =>
    arrangementGroupNumbers.add(arrangementGroup),
  );

  return Array.from(arrangementGroupNumbers);
};

type CustomerDetailsTabsProps = { selectedTab: CustomerDetailsTab };
export const CustomerDetailsTabs = ({
  selectedTab,
}: CustomerDetailsTabsProps) => {
  const navigate = useNavigate();
  const { generateCurrentCustomerUrl } = useCustomerUrlParam();
  return (
    <Tabs
      variant="line"
      index={TABS.findIndex(({ key }) => key === selectedTab)}
      colorScheme="blue"
      background="white"
    >
      <TabList>
        {TABS.map(({ label, route }) => (
          <Tab
            key={route}
            onClick={() => navigate(generateCurrentCustomerUrl(route))}
          >
            {label}
          </Tab>
        ))}
      </TabList>
    </Tabs>
  );
};

type CustomerDetailsTemplateProps = {
  selectedTab: CustomerDetailsTab;
  children: React.ReactNode;
};
export const CustomerDetailsTemplate = ({
  selectedTab,
  children,
}: CustomerDetailsTemplateProps) => {
  const toast = useToast();
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const { cifnum, generateCurrentCustomerUrl } = useCustomerUrlParam();

  const [
    customer,
    setCustomer,
  ] = useState<ValleyBankCustomerWithRelationships | null>(null);
  const [arrangementGroups, setArrangementGroups] = useState<string[]>([]);

  useGetValleyBankCustomerQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { cifNumber: cifnum || '', reportingDate, isPreview },
    onCompleted: (res) => {
      const customerJSON = res.valleyBankCustomer?.customer;
      if (customerJSON) {
        try {
          const newCustomer = parse.parseValleyBankCustomer(customerJSON);
          // TODO add in collateral accounts
          if (newCustomer) {
            const newArrangementGroups = findArrangementGroupNumbers(
              newCustomer,
            );
            if (newArrangementGroups.length > 0) {
              setArrangementGroups(newArrangementGroups);
            }
            setAsOfDate(newCustomer.asOfDate);
            setCustomer({ ...newCustomer });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err);
          throw new Error('Failed to parse customer');
        }
      }
    },
  });

  const breadCrumbs = [
    { label: 'Customers', path: '/valley-bank/customers' },
    {
      label: `CIF Number: ${cifnum}`,
      path:
        selectedTab === 'summary'
          ? undefined
          : generateCurrentCustomerUrl('/valley-bank/customers/summary'),
    },
  ];

  if (selectedTab === 'holdings') {
    breadCrumbs.push({
      label: 'Holdings',
      path: generateCurrentCustomerUrl('/valley-bank/customers/holdings'),
    });
  }

  if (selectedTab === 'info') {
    breadCrumbs.push({
      label: 'Customer Information',
      path: generateCurrentCustomerUrl('/valley-bank/customers/info'),
    });
  }

  return (
    <>
      <VStack spacing="32px" w="100%" alignItems="flex-start">
        <Breadcrumbs breadcrumbs={breadCrumbs} />
        <Heading as="h1" size="lg">
          {customer?.full_name}
        </Heading>
        <HStack spacing="32px" alignItems="flex-start">
          <VStack spacing="8px" alignItems="flex-start" flexGrow={1}>
            <HStack
              spacing="8px"
              borderBottom="1px"
              borderColor={colors.black[50]}
              color={colors.black[70]}
            >
              <Icon icon="hashtag" />
              <Text>CIF Number</Text>
            </HStack>
            <CustomerSummaryLink cifnum={cifnum || ''}>
              {cifnum}
            </CustomerSummaryLink>
          </VStack>
          <VStack spacing="8px" alignItems="flex-start">
            <HStack
              spacing="8px"
              borderBottom="1px"
              borderColor={colors.black[50]}
              color={colors.black[70]}
            >
              <Icon icon="folder-tree" />
              <Text>Arrangement Group</Text>
            </HStack>
            {arrangementGroups.length === 0 && (
              <Text> No arrangement groups found</Text>
            )}
            {arrangementGroups.length > 0 && (
              <HStack spacing="16px">
                {arrangementGroups.map((arrangementGroup) => (
                  <ArrangementGroupSummaryLink
                    arrangementGroup={arrangementGroup}
                    key={arrangementGroup}
                  >
                    {arrangementGroup}
                  </ArrangementGroupSummaryLink>
                ))}
              </HStack>
            )}
          </VStack>
        </HStack>
      </VStack>
      <CustomerDetailsTabs selectedTab={selectedTab} />
      <VStack w="100%" alignItems="flex-start" m="40px 0" spacing="24px">
        {children}
      </VStack>
    </>
  );
};
